export function checkEditability(column, data) {
    try {
        const columnOptions = column?.normalizedColumnOptions ?? {};
        if (!columnOptions || Object.keys(columnOptions).length == 0) return false;

        const canEdit = window.appStore?.containerStore?.permissions?.canEdit(column?.id, data)[0];
        const isFormulaOrCodeSnippet = column?.column_type_id == '19' || column?.column_type_id == '7';
        const isColumnOnlyReadable = columnOptions?.is_read_only || !column.hasOwnProperty("column_type_id");
        const isDefaultColumn = column?.default_type == '1' || ['created_at', 'updated_at'].includes(column?.id) || (column.alias_name == 'createdBy' && column?.default_type == '1') || (column.alias_name == 'star' || columnOptions?.is_star_column);

        return canEdit && !isFormulaOrCodeSnippet && !isColumnOnlyReadable && !isDefaultColumn;
    } catch (err) {
        console.log('Error checking editability: ', err);
        return false;
    }
}

function checkIsMandatory(columnOptions) {
    return columnOptions?.is_mandatory ?? false;
}

function checkEmptyTextValue(value) {
    return value === "";
}

function checkIfValueIsInAllowedLength(value, columnOptions) {
    if ((columnOptions?.min_length === null && columnOptions?.max_length === null) || (columnOptions?.min_length === undefined && columnOptions?.max_length === undefined)) return true;
    
    const len = value.length;
    const max = columnOptions?.max_length ? Number(columnOptions.max_length) : Infinity;
    const min = columnOptions?.min_length ? Number(columnOptions.min_length) : 0;

    return len <= max && len >= min;
}

function checkIfValueIsInAllowedRange(value, column) {
    const columnOptions = column?.normalizedColumnOptions ?? {};
    if ((columnOptions?.min_value === null && columnOptions?.max_value === null) || (columnOptions?.min_value === undefined && columnOptions?.max_value === undefined)) return true;
    const minVal = (column?.column_type_id == '22' && !isNaN(Number(columnOptions?.min_value))) ? Number(columnOptions?.min_value)/100 : Number(columnOptions?.min_value);
    const maxVal = (column?.column_type_id == '22' && !isNaN(Number(columnOptions?.max_value))) ? Number(columnOptions?.max_value)/100: Number(columnOptions?.max_value);
    return Number(value) >= Number(minVal) && Number(value) <= Number(maxVal);
}

export function validateEmail(email) {
    const regex = /^(([^<>()\[\]\\.,;\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(email);
};

export function validateTextTypeInputs(value, column) {
    if (checkIsMandatory(column?.normalizedColumnOptions) && checkEmptyTextValue(value)) {
        return { isInvalid: true, message: "This value can't be empty" };
    }

    if (column?.column_type_id == "14" && value !== "") {
        const isInvalidEmail = !validateEmail(value);
        if (isInvalidEmail) {
            return { isInvalid: true, message: "Please enter a valid email address" };
        }
    }

    const isTextOrPhoneNumber = column?.column_type_id === '1' || column?.column_type_id === '20';
    if (isTextOrPhoneNumber) {
        const minLen = column?.normalizedColumnOptions?.min_length || 0;
        if (value.length < minLen) {
            return { isInvalid: true, message: `Minimum ${minLen} characters required.` };
        }
    }

    return { isInvalid: false, message: "" };
}

export function validateTextTypeInputsBeforeSaving(value, column, data) {
    const hasPermissions = checkEditability(column, data);
    if (!hasPermissions) return {'isInvalid': true, 'message': 'You do not have permission to edit this field.'};

    let isValueMandatory = checkIsMandatory(column?.normalizedColumnOptions);
    if (isValueMandatory && value === "") return {'isInvalid': true, 'message': "This value can't be empty"};

    if (column.column_type_id == '1' || column.column_type_id == '20') {
        return checkIfValueIsInAllowedLength(value, column?.normalizedColumnOptions) ? {'isInvalid': false, 'message': ""} : {'isInvalid': true, 'message': "Value entered is not in allowed length."};
    }
    
    return {'isInvalid': false, 'message': ""};
}

export function validateNumberInputsBeforeSaving(value, column, data) {
    const hasPermissions = checkEditability(column, data);
    if (!hasPermissions) return {'isInvalid': true, 'message': 'You do not have permission to edit this field.'};

    let isValueMandatory = checkIsMandatory(column?.normalizedColumnOptions);
    if (isValueMandatory && Number(value) === 0) return {'isInvalid': true, 'message': "This value can't be empty"};

    const isValueInAllowedLength = checkIfValueIsInAllowedLength(value, column?.normalizedColumnOptions);
    if (!isValueInAllowedLength) return {'isInvalid': true, 'message': "Value entered is not in allowed length."};

    const isValueInAllowedRange = checkIfValueIsInAllowedRange(value, column);
    if (!isValueInAllowedRange) return {'isInvalid': true, 'message': "Value entered is not in allowed range."};

    return {'isInvalid': false, 'message': ""};
}

export function validateDateInputsBeforeSaving(value, column, data) {
    const hasPermissions = checkEditability(column, data);
    if (!hasPermissions) return {'isInvalid': true, 'message': 'You do not have permission to edit this field.'};

    let isValueMandatory = checkIsMandatory(column?.normalizedColumnOptions);
    if (isValueMandatory && (value === "" || value === "0000-00-00" || value === "0000-00-00 00:00:00" || value === "0000-00-00T00:00:00")) return {'isInvalid': true, 'message': "This value can't be empty"};

    return {'isInvalid': false, 'message': ""};
}

export function validateBooleanInputsBeforeSaving(column, data) {
    const hasPermissions = checkEditability(column, data);
    if (!hasPermissions) return {'isInvalid': true, 'message': 'You do not have permission to edit this field.'};

    return {'isInvalid': false, 'message': ""};
}

export function validateDropdownInputsBeforeSaving(value, column, data) {
    const hasPermissions = checkEditability(column, data);
    if (!hasPermissions) return {'isInvalid': true, 'message': 'You do not have permission to edit this field.'};

    let isValueMandatory = checkIsMandatory(column?.normalizedColumnOptions);
    if (isValueMandatory && (value === "" || !value)) return {'isInvalid': true, 'message': "This value can't be empty"};

    return {'isInvalid': false, 'message': ""};
}